import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useDropdowns } from "@/contexts/DropdownContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserSearchSelect } from "@/components/UserSearchSelect";
import { SortableHeader, TablePagination } from "@/components/TableComponents";
import { useTableSort } from "@/hooks/useTableSort";
import { Plus, Search, Edit, Trash2, User, ArrowUpRight, FolderKanban } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export default function LeadsPage() {
  const navigate = useNavigate();
  const { token, hasPermission, user } = useAuth();
  const { getOptions, getColor } = useDropdowns();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [filterStage, setFilterStage] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promotingLead, setPromotingLead] = useState(null);
  const [manualProjectId, setManualProjectId] = useState("");
  
  const isAdmin = user?.role === "Admin";

  // Get dropdown options from context
  const stageOptions = getOptions("leads", "stage");
  const categoryOptions = getOptions("leads", "category");
  const sourceOptions = getOptions("leads", "source");

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    source: "Website",
    brand: "Brillien Premium",
    phone: "",
    email: "",
    city: "",
    state: "",
    category: "Office Interiors",
    area: "",
    budget: "",
    stage: "New",
    assigned_to_id: "",
    assigned_to_name: "",
  });

  useEffect(() => {
    fetchLeads();
    if (isAdmin) {
      fetchUsersAndEmployees();
    }
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersAndEmployees = async () => {
    try {
      const [usersRes, employeesRes] = await Promise.all([
        axios.get(`${API}/auth/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/employees`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (e) {
      console.error("Failed to fetch users/employees");
    }
  };

  const handleAssigneeChange = (id, name) => {
    setFormData({
      ...formData,
      assigned_to_id: id,
      assigned_to_name: name
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLead) {
        await axios.put(`${API}/leads/${editingLead.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Lead updated successfully");
      } else {
        await axios.post(`${API}/leads`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Lead created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lead deleted successfully");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handlePromoteToProject = async (lead) => {
    setPromotingLead(lead);
    setManualProjectId("");
    setPromoteDialogOpen(true);
  };

  const confirmPromoteToProject = async () => {
    if (!promotingLead) return;
    try {
      const response = await axios.post(`${API}/leads/${promotingLead.id}/promote-to-project`, 
        { project_id: manualProjectId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Lead promoted to project successfully!`);
      setPromoteDialogOpen(false);
      setPromotingLead(null);
      setManualProjectId("");
      fetchLeads();
      // Navigate to the new project
      if (response.data.project_id) {
        navigate(`/projects/${response.data.project_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to promote lead to project");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      company: "",
      source: "Website",
      brand: "Brillien Premium",
      phone: "",
      email: "",
      city: "",
      state: "",
      category: "Office Interiors",
      area: "",
      budget: "",
      stage: "New",
      assigned_to_id: "",
      assigned_to_name: "",
    });
    setEditingLead(null);
  };

  const openEditDialog = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      company: lead.company,
      source: lead.source,
      brand: lead.brand,
      phone: lead.phone,
      email: lead.email,
      city: lead.city,
      state: lead.state,
      category: lead.category,
      area: lead.area,
      budget: lead.budget,
      stage: lead.stage,
      assigned_to_id: lead.assigned_to_id || "",
      assigned_to_name: lead.assigned_to_name || "",
    });
    setDialogOpen(true);
  };

  const canWrite = hasPermission("leads:write");
  const canDelete = hasPermission("leads:delete");

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.assigned_to_name && lead.assigned_to_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStage = filterStage === "all" || lead.stage === filterStage;
    const matchesCategory = filterCategory === "all" || lead.category === filterCategory;
    const matchesSource = filterSource === "all" || lead.source === filterSource;
    
    return matchesSearch && matchesStage && matchesCategory && matchesSource;
  });

  // Sorting and pagination
  const {
    sortKey,
    sortOrder,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    startIndex,
    endIndex,
    paginatedData,
    totalItems
  } = useTableSort(filteredLeads, {
    defaultSortKey: "created_at",
    defaultSortOrder: "desc",
    defaultPageSize: 10
  });

  const clearFilters = () => {
    setFilterStage("all");
    setFilterCategory("all");
    setFilterSource("all");
    setSearchTerm("");
  };

  const hasActiveFilters = filterStage !== "all" || filterCategory !== "all" || filterSource !== "all" || searchTerm !== "";

  if (loading) {
    return <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen text-slate-600 dark:text-slate-400">Loading...</div>;
  }

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Lead Management
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Track and manage all potential clients
          </p>
        </div>
        {canWrite && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-lead-button"
                className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4 font-medium"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
              <DialogHeader>
                <DialogTitle>{editingLead ? "Edit Lead" : "Add New Lead"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="rounded-lg"
                      data-testid="lead-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company">Company *</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      required
                      className="rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="rounded-lg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      required
                      className="rounded-lg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      required
                      className="rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="source">Source *</Label>
                    <Select value={formData.source} onValueChange={(value) => setFormData({...formData, source: value})}>
                      <SelectTrigger className="rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sourceOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="brand">Brand *</Label>
                    <Select value={formData.brand} onValueChange={(value) => setFormData({...formData, brand: value})}>
                      <SelectTrigger className="rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Brillien Premium">Brillien Premium</SelectItem>
                        <SelectItem value="Brillien Classic">Brillien Classic</SelectItem>
                        <SelectItem value="Brillien Standard">Brillien Standard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                      <SelectTrigger className="rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="area">Approximate Area *</Label>
                    <Input
                      id="area"
                      value={formData.area}
                      onChange={(e) => setFormData({...formData, area: e.target.value})}
                      placeholder="e.g., 5,500 sq.ft"
                      required
                      className="rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="budget">Budget *</Label>
                    <Input
                      id="budget"
                      value={formData.budget}
                      onChange={(e) => setFormData({...formData, budget: e.target.value})}
                      placeholder="e.g., ₹85 Lakhs"
                      required
                      className="rounded-lg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="stage">Stage *</Label>
                    <Select value={formData.stage} onValueChange={(value) => setFormData({...formData, stage: value})}>
                      <SelectTrigger className="rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {stageOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Assignment Section */}
                {isAdmin && (
                  <div className="pt-4 border-t border-slate-200">
                    <Label className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4 text-slate-500" />
                      Assign Lead To
                    </Label>
                    <UserSearchSelect
                      users={users}
                      employees={employees}
                      currentUser={user}
                      value={formData.assigned_to_id}
                      onChange={handleAssigneeChange}
                      placeholder="Search and select assignee..."
                      allowUnassigned={true}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="lead-submit-button" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg">
                    {editingLead ? "Update" : "Create"} Lead
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search leads by name, company, ID, or assigned person..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="leads-search-input"
                className="pl-10 h-10 rounded-lg border-slate-300 dark:border-slate-400 dark:bg-slate-700 dark:text-white"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filters:</span>
            <Select value={filterStage} onValueChange={setFilterStage}>
              <SelectTrigger className="w-36 h-9 rounded-lg" data-testid="filter-stage">
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {stageOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-40 h-9 rounded-lg" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categoryOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="w-36 h-9 rounded-lg" data-testid="filter-source">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {sourceOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 dark:text-slate-400 h-9">
                Clear All
              </Button>
            )}
            <span className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
              {filteredLeads.length} of {leads.length} leads
            </span>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-700/50">
              <TableHead>
                <SortableHeader sortKey="id" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Lead ID
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="name" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Name / Company
                </SortableHeader>
              </TableHead>
              <TableHead className="font-semibold">Contact</TableHead>
              <TableHead>
                <SortableHeader sortKey="city" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Location
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="category" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Category
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="budget" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Budget
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="stage" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Stage
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="assigned_to_name" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Assigned To
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="created_at" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Created
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="updated_at" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Updated
                </SortableHeader>
              </TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((lead) => (
              <TableRow key={lead.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50" data-testid={`lead-row-${lead.id}`}>
                <TableCell className="font-mono text-sm text-slate-600 dark:text-slate-400">{lead.id}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{lead.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{lead.company}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p className="text-slate-700 dark:text-slate-300">{lead.phone}</p>
                    <p className="text-slate-500 dark:text-slate-400">{lead.email}</p>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-700 dark:text-slate-300">{lead.city}, {lead.state}</TableCell>
                <TableCell className="text-sm text-slate-700 dark:text-slate-300">{lead.category}</TableCell>
                <TableCell className="font-mono text-sm text-slate-900 dark:text-white">{lead.budget}</TableCell>
                <TableCell>
                  <Badge className={`${getColor("leads", "stage", lead.stage)} border text-xs`}>{lead.stage}</Badge>
                </TableCell>
                <TableCell>
                  {lead.assigned_to_name ? (
                    <span className="text-sm text-brillien-700 dark:text-brillien-400 font-medium">{lead.assigned_to_name}</span>
                  ) : (
                    <span className="text-sm text-slate-400 dark:text-slate-500">Unassigned</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-600 dark:text-slate-400">{formatDate(lead.created_at)}</TableCell>
                <TableCell className="text-sm text-slate-600 dark:text-slate-400">{formatDate(lead.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {/* Promote to Project button - only for Won leads */}
                    {canWrite && lead.stage === "Won" && !lead.promoted_to_project_id && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handlePromoteToProject(lead)} 
                        className="h-8 px-2 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                        title="Promote to Project"
                        data-testid={`promote-lead-${lead.id}`}
                      >
                        <FolderKanban className="h-4 w-4 mr-1" />
                        <span className="text-xs">Promote</span>
                      </Button>
                    )}
                    {/* Already promoted badge */}
                    {lead.promoted_to_project_id && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => navigate(`/projects/${lead.promoted_to_project_id}`)}
                        className="h-8 px-2 text-blue-600 hover:bg-blue-50"
                        title="View Project"
                      >
                        <ArrowUpRight className="h-4 w-4 mr-1" />
                        <span className="text-xs">Project</span>
                      </Button>
                    )}
                    {canWrite && (
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(lead)} className="h-8 w-8 p-0">
                        <Edit className="h-4 w-4 text-slate-600" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(lead.id)} className="h-8 w-8 p-0">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {paginatedData.length === 0 && (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            <User className="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
            <p>No leads found</p>
            {canWrite && <p className="text-sm mt-1">Click &quot;Add New Lead&quot; to create one</p>}
          </div>
        )}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          startIndex={startIndex}
          endIndex={endIndex}
          totalItems={totalItems}
        />
      </Card>

      {/* Promote to Project Dialog */}
      <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle>Promote Lead to Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              You are about to promote lead <span className="font-semibold">"{promotingLead?.name}"</span> from <span className="font-semibold">{promotingLead?.company}</span> to a new project.
            </p>
            <div className="bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-400">
              <Label htmlFor="promote-project-id" className="text-sm font-medium">
                Project ID <span className="text-slate-400 font-normal">(Optional - leave blank for auto-generated ID)</span>
              </Label>
              <Input
                id="promote-project-id"
                value={manualProjectId}
                onChange={(e) => setManualProjectId(e.target.value)}
                placeholder="e.g., PRJ-2025-001"
                className="rounded-sm mt-2"
                data-testid="promote-manual-project-id"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPromoteDialogOpen(false)} className="rounded-lg">
                Cancel
              </Button>
              <Button onClick={confirmPromoteToProject} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg" data-testid="confirm-promote-btn">
                <FolderKanban className="h-4 w-4 mr-2" />
                Create Project
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
